<template>
  <div class="redirectsettings">
    <div class="row">
      <div class="col-xs-12">
        <h3>{{ _('Redirects') }}</h3>
        <div class="well">
          {{ _('Send visitors from old addresses, for example from your previous website, to pages in your online store. Enter the address without the domain and language code, for example /old-page. One redirect applies in every language. If the old address has parameters, include them, for example /index.php?page_id=12.') }}
        </div>
      </div>
    </div>
    <form class="row redirect-form" v-on:submit.prevent="save">
      <div class="col-md-3">
        <label>{{ _('Type') }}</label>
        <select class="form-control" v-model.number="form.status">
          <option v-for="status in statuses" :key="status.value" :value="status.value">{{ status.name }}</option>
        </select>
      </div>
      <div class="col-md-4">
        <label>{{ _('Old address') }}</label>
        <input type="text" class="form-control" v-model="form.orig" placeholder="/old-page" />
      </div>
      <div class="col-md-5" v-if="needsDestination">
        <label>{{ _('New address') }}</label>
        <input type="text" class="form-control" v-model="form.dest" :placeholder="_('/summer or https://...')" />
      </div>
      <div class="col-md-12 redirect-buttons">
        <button type="submit" class="btn btn-success" :disabled="saving || !form.orig">
          <i class="fa fa-fw fa-check"></i> {{ form.id ? _('Save') : _('Add') }}
        </button>
        <button type="button" class="btn btn-default" v-if="form.id" v-on:click="resetForm">
          <i class="fa fa-fw fa-close"></i> {{ _('Cancel') }}
        </button>
      </div>
    </form>
    <p v-if="loading" class="text-muted"><i class="fa fa-fw fa-cog fa-spin"></i> {{ _('Loading...') }}</p>
    <table v-else class="table table-striped">
      <thead>
        <tr>
          <th>{{ _('Old address') }}</th>
          <th>{{ _('New address') }}</th>
          <th>{{ _('Type') }}</th>
          <th>&nbsp;</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="redirect in redirects" :key="redirect.id" style="cursor: pointer" v-on:click="edit(redirect)">
          <td>{{ redirect.orig }}</td>
          <td>{{ redirect.dest || '–' }}</td>
          <td>{{ statusName(redirect.status) }}</td>
          <td><a href="javascript:;" class="pull-right" :title="_('Delete')" v-on:click.stop="remove(redirect)"><i class="fa fa-fw fa-close"></i></a></td>
        </tr>
        <tr v-if="!redirects.length">
          <td colspan="4"><i>{{ _('No redirects have been added.') }}</i></td>
        </tr>
      </tbody>
    </table>

    <div class="row">
      <div class="col-xs-12">
        <h3>{{ _('Changed addresses') }}</h3>
        <div class="well">
          {{ _('When the direct URL of a product, category or collection changes, its old address keeps working and takes visitors to the new one. Delete an old address that should no longer work, for example a typo, and visitors to it will see that the page was not found.') }}
        </div>
      </div>
    </div>
    <div class="row redirect-history-search">
      <div class="col-md-4">
        <input type="text" class="form-control" v-model="historySearch" :placeholder="_('Search')" v-on:input="scheduleHistoryLoad" />
      </div>
    </div>
    <table class="table table-striped">
      <thead>
        <tr>
          <th>{{ _('Old address') }}</th>
          <th>{{ _('Current address') }}</th>
          <th>{{ _('Item') }}</th>
          <th>{{ _('Changed') }}</th>
          <th>&nbsp;</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in history" :key="row.id">
          <td>{{ historyPath(row, row.path) }}</td>
          <td>{{ row.current ? historyPath(row, row.current) : '–' }}</td>
          <td>{{ row.name || '–' }} <span class="text-muted">({{ typeName(row.type) }})</span></td>
          <td>{{ formatChanged(row.created) }}</td>
          <td><a href="javascript:;" class="pull-right" :title="_('Delete')" v-on:click="removeHistory(row)"><i class="fa fa-fw fa-close"></i></a></td>
        </tr>
        <tr v-if="!history.length">
          <td colspan="5"><i>{{ _('No addresses have changed.') }}</i></td>
        </tr>
      </tbody>
    </table>
    <div v-if="historyTotal > historyLimit" class="redirect-history-paging">
      <button type="button" class="btn btn-default" :disabled="historyOffset === 0" v-on:click="historyPage(-1)"><i class="fa fa-fw fa-chevron-left"></i></button>
      {{ historyOffset + 1 }}–{{ Math.min(historyOffset + historyLimit, historyTotal) }} / {{ historyTotal }}
      <button type="button" class="btn btn-default" :disabled="historyOffset + historyLimit >= historyTotal" v-on:click="historyPage(1)"><i class="fa fa-fw fa-chevron-right"></i></button>
    </div>
  </div>
</template>

<script>

import axios from 'axios'
import { DateTime } from 'luxon'
import { mapGetters } from 'vuex'
import errors from '~/lib/errors'

// Storefront settings tab for site admins: manual redirects, and old direct URLs that redirect to the current one
export default {
  mixins: [errors],
  data () {
    return {
      loading: false,
      saving: false,
      redirects: [],
      form: this.emptyForm(),
      history: [],
      historyTotal: 0,
      historyOffset: 0,
      historyLimit: 50,
      historySearch: '',
      historyTimer: null
    }
  },
  computed: {
    ...mapGetters(['baseDomain', 'shopId', 'apiKey']),
    statuses () {
      return [
        { value: 301, name: this._('Permanent redirect (301)') },
        { value: 302, name: this._('Temporary redirect (302)') },
        { value: 410, name: this._('Removed page (410)') },
        { value: 404, name: this._('Page not found (404)') }
      ]
    },
    needsDestination () {
      return this.form.status === 301 || this.form.status === 302
    }
  },
  methods: {
    emptyForm () {
      return { id: null, status: 301, orig: '', dest: '' }
    },
    resetForm () {
      this.form = this.emptyForm()
    },
    url (path, query = '') {
      return this.baseDomain + this.shopId + path + '?apikey=' + this.apiKey + query
    },
    statusName (status) {
      const found = this.statuses.find(s => s.value === parseInt(status))
      return found ? found.name : status
    },
    typeName (type) {
      switch (type) {
        case 'product':
          return this._('Product')
        case 'category':
          return this._('Category')
        case 'collection':
          return this._('Collection')
      }
      return type
    },
    historyPath (row, path) {
      const collection = row.type === 'collection' ? 'collection-' : ''
      return (row.locale ? '/' + row.locale : '') + '/' + collection + path
    },
    formatChanged (created) {
      const date = DateTime.fromSQL(created || '')
      return date.isValid ? date.toLocaleString(DateTime.DATE_SHORT) : ''
    },
    edit (redirect) {
      this.form = {
        id: redirect.id,
        status: parseInt(redirect.status) || 301,
        orig: redirect.orig,
        dest: redirect.dest || ''
      }
    },
    async check (path) {
      const response = await axios.get(this.url('/redirects.json', '&check=1&path=' + encodeURIComponent(path)))
      return response.data || {}
    },
    // Front page or a bare language code: always a valid destination, the check doesn't cover it
    isFrontPage (path) {
      return /^\/([a-z]{2}_[A-Z]{2})?\/?$/.test(path.trim())
    },
    async confirmSave () {
      const orig = await this.check(this.form.orig)
      if (orig.live) {
        const name = orig.category ? orig.category + ' / ' + orig.name : orig.name
        if (!confirm(this._('The old address is a page in your online store: %s. Its visitors will no longer see it. Save anyway?').replace('%s', name))) {
          return false
        }
      }
      const dest = (this.form.dest || '').trim()
      if (this.needsDestination && dest.startsWith('/') && !dest.startsWith('//') && !this.isFrontPage(dest)) {
        const target = await this.check(dest)
        if (!target.live && !confirm(this._('No page was found at the new address. Save anyway?'))) {
          return false
        }
      }
      return true
    },
    async save () {
      this.saving = true
      try {
        if (!await this.confirmSave()) {
          return
        }
        const data = {
          status: this.form.status,
          orig: this.form.orig,
          dest: this.needsDestination ? this.form.dest : null
        }
        if (this.form.id) {
          await axios.put(this.url('/redirects/' + this.form.id + '.json'), data)
        } else {
          await axios.post(this.url('/redirects.json'), data)
        }
      } catch (e) {
        this.showError(e)
        return
      } finally {
        this.saving = false
      }
      this.showNotification(this._('Changes saved successfully!'))
      this.resetForm()
      this.loadRedirects()
    },
    async remove (redirect) {
      if (!confirm(this._('Delete the redirect from %s?').replace('%s', redirect.orig))) {
        return
      }
      try {
        await axios.delete(this.url('/redirects/' + redirect.id + '.json'))
      } catch (e) {
        this.showError(e)
        return
      }
      if (this.form.id === redirect.id) {
        this.resetForm()
      }
      this.redirects = this.redirects.filter(r => r.id !== redirect.id)
    },
    async loadRedirects () {
      this.loading = true
      try {
        const response = await axios.get(this.url('/redirects.json'))
        const rows = response.data || []
        this.redirects = Array.isArray(rows) ? rows : Object.values(rows)
      } catch (e) {
        this.showError(e)
      } finally {
        this.loading = false
      }
    },
    scheduleHistoryLoad () {
      if (this.historyTimer) {
        clearTimeout(this.historyTimer)
      }
      this.historyTimer = setTimeout(() => {
        this.historyOffset = 0
        this.loadHistory()
      }, 400)
    },
    historyPage (direction) {
      this.historyOffset = Math.max(0, this.historyOffset + direction * this.historyLimit)
      this.loadHistory()
    },
    async loadHistory () {
      const query = '&limit=' + this.historyLimit + '&offset=' + this.historyOffset + (this.historySearch ? '&search=' + encodeURIComponent(this.historySearch) : '')
      try {
        const response = await axios.get(this.url('/aliashistory.json', query))
        this.history = (response.data && response.data.rows) || []
        this.historyTotal = (response.data && parseInt(response.data.total)) || 0
      } catch (e) {
        this.showError(e)
      }
    },
    async removeHistory (row) {
      if (!confirm(this._('The old address %s will stop working. Delete it?').replace('%s', this.historyPath(row, row.path)))) {
        return
      }
      try {
        await axios.delete(this.url('/aliashistory/' + row.id + '.json'))
      } catch (e) {
        this.showError(e)
        return
      }
      this.loadHistory()
    }
  },
  created () {
    this.loadRedirects()
    this.loadHistory()
  },
  beforeUnmount () {
    if (this.historyTimer) {
      clearTimeout(this.historyTimer)
    }
  }
}
</script>

<style scoped>
.redirect-form {
  margin-bottom: 15px;
}
.redirect-buttons {
  margin-top: 10px;
}
.redirect-history-search {
  margin-bottom: 10px;
}
.redirect-history-paging {
  text-align: center;
}
</style>
